import React, { useEffect, useMemo } from 'react'
import { Divider, Grid, Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import messages from '../../../messages'
import SearchCriteria from './SearchCriteria/SearchCriteria'
import SearchResult from './SearchResult/SearchResult'
import GlobalAlert from '@src/components/GlobalAlert'
import { FormattedMessage } from 'react-intl'
import { useFormikContext } from 'formik'
import { MilliplexFormValues } from '../Milliplex'
import { useRouter } from '@src/routes'
import { useMilliplexKitsLazyQuery } from '@src/queries/MilliplexKitsQuery.generated'
import { Skeleton } from '@material-ui/lab'
import { InstrumentTypeItem } from '@src/types/graphql-types'
import { PlateTypeWithStatus } from '../utils'
import { getDefaultOrConvertToArray } from '@src/routes/Configurators/utils/util'

const useStyles = makeStyles((theme: Theme) => {
  return {
    errorAlert: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(6),
    },
    skeletonCardContainer: {
      height: theme.typography.pxToRem(200),
    },
    skeletonContainer: {
      marginBottom: theme.spacing(6),
    },
    productSkeleton: {
      minWidth: '100%',
      height: theme.typography.pxToRem(270),
      display: 'block',
      border: 'solid 1px',
      borderColor: theme.palette.grey[500],
      borderRadius: '5px',
      marginLeft: 0,
      marginRight: 0,
    },
  }
})
interface MilliplexStep2Props {
  displayPageError: boolean
  setDisplayPageError: React.Dispatch<React.SetStateAction<boolean>>
  plateType: PlateTypeWithStatus[]
  instrumentType: InstrumentTypeItem[]
}
const MilliplexStep2: React.FC<MilliplexStep2Props> = ({
  displayPageError,
  setDisplayPageError,
  plateType,
  instrumentType,
}) => {
  const classes = useStyles()
  const formik = useFormikContext<MilliplexFormValues>()
  const { query } = useRouter()
  const memoizedAnalytes = useMemo(() => query.analytes, [query])
  const memoizedResearchAreas = useMemo(() => query.researchAreas, [query])
  const [fetchMilliplexKits, { loading, data, called }] =
    useMilliplexKitsLazyQuery()

  useEffect(() => {
    query.previouslyViewed &&
      !called &&
      fetchMilliplexKits({
        variables: {
          input: {
            page: 1,
            species: query.species,
            researchAreas: getDefaultOrConvertToArray(query?.researchAreas),
            analytes: getDefaultOrConvertToArray(query?.analytes),
            panelId: query.previouslyViewed,
            count: 25,
          },
        },
      })
  }, [memoizedAnalytes, memoizedResearchAreas])

  const previouslyViewedKit = query.previouslyViewed
    ? data?.getMilliplexKits?.listKits.find(
        (kit) => kit.panelId === query.previouslyViewed
      )
    : null
  const previousCallStatus = query.previouslyViewed ? called : true
  return (
    <>
      {loading && (
        <Grid container className={classes.skeletonContainer}>
          {Array(1)
            .fill(undefined)
            .map((_, index) => (
              <Grid
                key={index}
                xs={12}
                item
                className={classes.skeletonCardContainer}
              >
                <Skeleton
                  animation="wave"
                  className={classes.productSkeleton}
                />
              </Grid>
            ))}
        </Grid>
      )}
      {!loading && previousCallStatus && (
        <>
          {displayPageError &&
            Object.keys(formik.values.selectedKits).length === 0 &&
            !formik.errors.plateType &&
            !formik.errors.instrumentType &&
            !formik.errors.analytes && (
              <GlobalAlert severity="error" className={classes.errorAlert}>
                <FormattedMessage
                  defaultMessage="Please select a kit before proceeding to next step."
                  id="MILLIPLEX_KIT_ERROR_MESSAGE"
                />
              </GlobalAlert>
            )}
          {displayPageError &&
            (formik.errors.plateType ||
              formik.errors.instrumentType ||
              formik.errors.analytes) && (
              <GlobalAlert severity="error" className={classes.errorAlert}>
                <FormattedMessage {...messages.CONF_DNAPLATES_ERROR_STEP2} />
              </GlobalAlert>
            )}
          <SearchCriteria previouslyViewedKit={previouslyViewedKit} />
          <Divider style={{ marginTop: '50px' }} />
          <SearchResult
            previouslyViewedKit={previouslyViewedKit}
            displayPageError={displayPageError}
            setDisplayPageError={setDisplayPageError}
            plateType={plateType}
            instrumentType={instrumentType}
          />
        </>
      )}
    </>
  )
}

export default MilliplexStep2
